import type {
  ConflictDetection,
  HotkeyConfig,
  HotkeyConflict,
  HotkeyManagerConfig,
  HotkeyMatch,
  HotkeyRegistryEntry,
  KeyModifier,
  NormalizedKeyEvent,
  UserPreferences,
} from "../types";
import { DEFAULT_CONFIG, EVENTS } from "../types/defaults";
import { createLogger } from "../utils/devLogger";
import { createHotkeyKey, hotkeyMatches, isInputElement, normalizeKeyEvent } from "../utils/keyNormalizer";
import { validateHotkey, validateHotkeys } from "../utils/wcagValidation";
import { EventEmitter } from "./EventEmitter";
import { PreferencesManager } from "./preferences";
import { createStorage } from "./storage";

const logger = createLogger("[HotkeyManager]");

export class HotkeyManager extends EventEmitter {
  private config: Required<HotkeyManagerConfig>;
  private registry: Map<string, HotkeyRegistryEntry>;
  private preferences: PreferencesManager;
  private activeScope: string;
  private listener: ((e: KeyboardEvent) => void) | null;
  private isListening: boolean;

  private registryVersion = 0;
  // Only cache hot-path operations to avoid premature optimization
  private findMatchesKeyCache: string | null = null;
  private findMatchesResultCache: HotkeyMatch[] | null = null;

  scopeManager: { getScopeStack: () => string[] };

  constructor(config: HotkeyManagerConfig = {}) {
    super();

    logger.info("Constructor called with config:", config);

    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
      storage: config.storage || createStorage(),
    };

    this.registry = new Map();
    this.preferences = new PreferencesManager(this.config.storage);
    this.activeScope = this.config.globalScope;
    this.listener = null;
    this.isListening = false;
    this.scopeManager = {
      getScopeStack: () => [this.activeScope],
    };

    logger.info("Initialized with globalScope:", this.activeScope);

    this.preferences.subscribe((prefs) => {
      this.invalidateCaches();
      logger.info("Preferences changed:", prefs);
      this.emit(EVENTS.PREFERENCES_TOGGLED, prefs.enabled);
    });
  }

  register(config: HotkeyConfig): string {
    logger.info("register: Registering hotkey:", config);
    const validation = validateHotkey(config);
    if (!validation.valid && this.config.logWarnings) {
      logger.warn("hotkey may not be wcag compliant:", validation.violations);
    }

    const id = this.generateId(config);
    logger.info("register: Generated ID:", id);

    const conflicts = this.detectConflicts(config);
    if (conflicts.hasConflicts) {
      logger.info("register: Conflicts detected:", conflicts);
      if (this.config.autoResolveConflicts) {
        this.resolveConflicts(config, conflicts);
      } else if (this.config.logWarnings) {
        logger.warn("hotkey conflicts detected:", conflicts.conflicts);
      }
    }

    const entry: HotkeyRegistryEntry = {
      id,
      config,
      originalConfig: { ...config },
      scope: config.scope || this.config.globalScope,
      enabled: config.enabled !== false,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    this.registry.set(id, entry);

    this.invalidateCaches();

    this.emit(EVENTS.REGISTERED, config);

    logger.info(`register: successfully registered: ${this.formatHotkey(config)} with id: ${id}`);

    return id;
  }

  unregister(id: string): boolean {
    logger.info("unregister: Unregistering ID:", id);
    const entry = this.registry.get(id);
    if (!entry) {
      logger.info("unregister: Entry not found for ID:", id);
      return false;
    }

    this.registry.delete(id);

    this.invalidateCaches();

    this.emit(EVENTS.UNREGISTERED, id);

    return true;
  }

  update(id: string, updates: Partial<HotkeyConfig>): boolean {
    const entry = this.registry.get(id);
    if (!entry) return false;

    const newConfig = {
      ...entry.config,
      ...updates,
    };

    const validation = validateHotkey(newConfig);
    if (!validation.valid) {
      logger.error("updated hotkey not wcag compliant:", validation.violations);
      return false;
    }

    entry.config = newConfig;
    entry.updatedAt = Date.now();
    this.registry.set(id, entry);

    this.invalidateCaches();

    this.emit(EVENTS.REMAPPED, entry.originalConfig, newConfig);

    return true;
  }

  enable(id: string): boolean {
    const entry = this.registry.get(id);
    if (!entry) return false;

    entry.enabled = true;
    entry.updatedAt = Date.now();
    this.registry.set(id, entry);

    this.invalidateCaches();

    return true;
  }

  disable(id: string): boolean {
    const entry = this.registry.get(id);
    if (!entry) return false;

    entry.enabled = false;
    entry.updatedAt = Date.now();
    this.registry.set(id, entry);

    this.invalidateCaches();

    return true;
  }

  start(): void {
    if (this.isListening) return;

    this.listener = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener("keydown", this.listener);
    this.isListening = true;

    logger.info("start: listener attached, now listening for hotkeys");
  }

  stop(): void {
    if (!this.isListening || !this.listener) {
      logger.info("stop: Not listening, skipping");
      return;
    }

    logger.info("stop: stopping keyboard event listener");
    document.removeEventListener("keydown", this.listener);
    this.listener = null;
    this.isListening = false;
  }

  setScope(scope: string): void {
    if (scope === this.activeScope) {
      logger.info("setScope: Scope unchanged:", scope);
      return;
    }

    const previousScope = this.activeScope;
    this.activeScope = scope;

    this.invalidateCaches();

    logger.info(`setScope: scope changed: ${previousScope} → ${scope}`);
  }

  setCurrentScope(scope: string): void {
    this.setScope(scope);
  }

  getScope(): string {
    return this.activeScope;
  }

  getActiveScope(): string {
    return this.activeScope;
  }

  activateScope(scope: string): void {
    this.setScope(scope);
  }

  deactivateScope(scope: string): void {
    if (this.activeScope === scope) {
      this.setScope(this.config.globalScope);
    }
  }

  getRegistry(): { registerCommand?: (command: unknown) => void; unregisterCommand?: (id: string) => void } | undefined {
    return undefined;
  }

  setPreferences(preferences: Partial<UserPreferences>): void {
    this.preferences.set(preferences);
    this.invalidateCaches();
  }

  getPreferencesSnapshot(): Readonly<UserPreferences> {
    return this.preferences.getPreferences();
  }

  getHotkeyByCombo(combo: string): HotkeyRegistryEntry | undefined {
    const parts = combo
      .split("+")
      .map((part) => part.trim())
      .filter(Boolean);

    const key = parts.pop() || "";
    const modifiers = parts as KeyModifier[];
    const signature = createHotkeyKey(key, modifiers);

    for (const entry of this.registry.values()) {
      const entrySignature = createHotkeyKey(entry.config.key, entry.config.modifiers);
      if (entrySignature === signature) {
        return entry;
      }
    }

    return undefined;
  }

  getHotkeys(scope?: string): HotkeyRegistryEntry[] {
    const entries = Array.from(this.registry.values());

    if (!scope) {
      return entries;
    }

    return entries.filter((e) => e.scope === scope);
  }

  getAllShortcuts(): HotkeyConfig[] {
    return Array.from(this.registry.values()).map((entry) => entry.config);
  }

  getHotkey(id: string): HotkeyRegistryEntry | undefined {
    return this.registry.get(id);
  }

  private detectConflicts(config: HotkeyConfig): ConflictDetection {
    const conflicts: ConflictDetection = {
      hasConflicts: false,
      conflicts: [],
    };

    const scope = config.scope || this.config.globalScope;
    const keySignature = createHotkeyKey(config.key, config.modifiers);

    for (const entry of this.registry.values()) {
      if (entry.scope !== scope) continue;

      const existingSignature = createHotkeyKey(entry.config.key, entry.config.modifiers);

      if (keySignature === existingSignature) {
        conflicts.hasConflicts = true;
        conflicts.conflicts.push({
          key: config.key,
          modifiers: config.modifiers || [],
          scope,
          conflictingHotkeys: [
            {
              id: entry.id,
              description: entry.config.description,
              priority: entry.config.priority || 0,
            },
          ],
        });
      }
    }

    return conflicts;
  }

  checkConflicts(): HotkeyConflict[] {
    const conflicts: HotkeyConflict[] = [];
    const signatureMap = new Map<string, HotkeyRegistryEntry[]>();

    for (const entry of this.registry.values()) {
      const scope = entry.scope || this.config.globalScope;
      const signature = `${scope}:${createHotkeyKey(entry.config.key, entry.config.modifiers)}`;
      const list = signatureMap.get(signature) || [];
      list.push(entry);
      signatureMap.set(signature, list);
    }

    for (const entries of signatureMap.values()) {
      if (entries.length < 2) continue;
      const first = entries[0];
      conflicts.push({
        key: first.config.key,
        modifiers: first.config.modifiers || [],
        scope: first.scope,
        conflictingHotkeys: entries.map((entry) => ({
          id: entry.id,
          description: entry.config.description,
          priority: entry.config.priority || 0,
        })),
      });
    }

    return conflicts;
  }

  private resolveConflicts(config: HotkeyConfig, conflicts: ConflictDetection): void {
    const newPriority = config.priority || 0;

    for (const conflict of conflicts.conflicts) {
      for (const conflicting of conflict.conflictingHotkeys) {
        if (newPriority > conflicting.priority) {
          this.disable(conflicting.id);

          if (this.config.logWarnings) {
            console.warn(
              `disabled conflicting hotkey "${conflicting.description}" ` + `(priority ${conflicting.priority} < ${newPriority})`,
            );
          }
        } else {
          if (this.config.logWarnings) {
            logger.warn(
              `hotkey conflict: "${config.description}" (priority ${newPriority}) `
                + `conflicts with "${conflicting.description}" (priority ${conflicting.priority})`,
            );
          }
        }
      }
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.preferences.isEnabled()) {
      return;
    }

    if (this.preferences.getPreferences().disableWhenInputFocused) {
      if (isInputElement(e.target as Element)) {
        return;
      }
    }

    const normalized = normalizeKeyEvent(e);

    const matches = this.findMatches(normalized);
    logger.info(`Found ${matches.length} match(es):`, matches);

    if (matches.length === 0) return;
    matches.sort((a, b) => {
      const priorityA = a.entry.config.priority || 0;
      const priorityB = b.entry.config.priority || 0;
      return priorityB - priorityA;
    });
    const match = matches[0];
    this.executeHotkey(match.entry, normalized);
  }

  private findMatches(event: NormalizedKeyEvent): HotkeyMatch[] {
    const cacheKey = this.createFindMatchesCacheKey(event);

    if (this.findMatchesKeyCache === cacheKey && this.findMatchesResultCache) {
      return this.findMatchesResultCache;
    }

    const matches: HotkeyMatch[] = [];

    for (const entry of this.registry.values()) {
      if (!entry.enabled) continue;

      if (entry.scope !== this.activeScope && entry.scope !== this.config.globalScope) {
        continue;
      }

      const customMapping = this.preferences.getCustomMapping(entry.id);
      const config = customMapping || entry.config;

      if (hotkeyMatches(event, config.key, config.modifiers)) {
        matches.push({
          entry,
          exactMatch: true,
          score: entry.config.priority || 0,
        });
      }
    }

    this.findMatchesKeyCache = cacheKey;
    this.findMatchesResultCache = matches;

    return matches;
  }

  private async executeHotkey(entry: HotkeyRegistryEntry, event: NormalizedKeyEvent): Promise<void> {
    const config = entry.config;

    if (config.preventDefault !== false) {
      event.originalEvent.preventDefault();
    }

    if (config.stopPropagation) {
      event.originalEvent.stopPropagation();
    }

    this.emit(EVENTS.TRIGGERED, config, event);

    if (this.preferences.getPreferences().announceToScreenReader) {
      this.announceToScreenReader(config.description);
    }

    try {
      await config.handler(event);
    } catch (error) {
      console.error("error executing hotkey:", error);
      this.emit(EVENTS.INVALID, config, error as Error);
    }
  }

  private announceToScreenReader(message: string): void {
    /**
     * Emit event for React components to handle screen reader announcements.
     * React applications should provide their own announcement component that
     * listens to this event and renders an ARIA live region.
     *
     * Example:
     *   manager.on('screenReaderAnnouncement', (msg) => {
     *     setAnnouncementText(msg);
     *   });
     *
     * Then render: <div role="status" aria-live="polite">{announcementText}</div>
     */
    this.emit("screenReaderAnnouncement", message);
  }

  private generateId(config: HotkeyConfig): string {
    const keyPart = createHotkeyKey(config.key, config.modifiers);
    const scopePart = config.scope || "global";
    const timestamp = Date.now();

    return `${scopePart}:${keyPart}:${timestamp}`;
  }

  private formatHotkey(config: HotkeyConfig): string {
    const modifiers = (config.modifiers || []).join("+");
    return modifiers ? `${modifiers}+${config.key}` : config.key;
  }

  validateAll(): ReturnType<typeof validateHotkeys> {
    const configs = Array.from(this.registry.values()).map((e) => e.config);
    return validateHotkeys(configs);
  }

  getPreferences(): PreferencesManager {
    return this.preferences;
  }

  destroy(): void {
    this.stop();
    this.registry.clear();
    this.invalidateCaches();
    this.removeAllListeners();
  }

  private invalidateCaches(): void {
    this.registryVersion++;
    this.findMatchesKeyCache = null;
    this.findMatchesResultCache = null;
  }

  private createFindMatchesCacheKey(event: NormalizedKeyEvent): string {
    const modifierString = Array.from(event.modifiers).sort().join("+");
    const keyString = modifierString ? `${modifierString}+${event.key}` : event.key;

    return `${keyString}:${this.activeScope}:${this.registryVersion}`;
  }
}
